import { createSVGElement } from '@/utils/svg-kit';
import type { BoundingBox } from '@/version-types';
import type { TextRenderSnapshot } from '../../types';
import { createFilterTarget, type TextFilter } from '../filter';

/**
 * 输出 SVG 中由 Pipeline 显式消费的命名挂载点。
 */
type TextSurface = Readonly<{
    root: SVGSVGElement;
    background: SVGGElement;
    material: SVGGElement;
    body: SVGGElement;
    characterMarker: SVGGElement;
    graphicMarker: SVGGElement;
}>;

/**
 * 读取正文与声明式素材挂载点统一使用的列表 marker 平移。
 *
 * @param snapshot - 当前一轮排版 snapshot。
 * @returns 横排或竖排列表对应的挂载点平移；无 marker 缩进时返回零偏移。
 */
export const resolveBodyOffset = (snapshot: TextRenderSnapshot): { x: number; y: number } =>
    snapshot.writingMode === 'vertical-rl'
        ? { x: 0, y: snapshot.listMarkerIndent }
        : { x: snapshot.listMarkerIndent, y: 0 };

/**
 * 读取声明式素材挂载点需要保持的既有列表 marker 平移。
 *
 * 历史 Highlight 对横排和竖排列表都沿 X 轴平移；花字只支持横排。该独立挂载点保留此行为，避免结构
 * 重组顺手改变竖排 Highlight 的视觉位置。
 *
 * @param snapshot - 当前一轮排版 snapshot。
 * @returns 素材挂载点沿 X 轴使用的列表 marker 平移。
 */
export const resolveMaterialOffset = (snapshot: TextRenderSnapshot): { x: number; y: number } => ({
    x: snapshot.listMarkerIndent,
    y: 0,
});

/**
 * 创建带稳定名称的空 SVG group 挂载点。
 *
 * @param name - 写入 data 属性的挂载点名称。
 * @param attributes - 挂载点创建时需要写入的其他属性。
 * @returns 可由单个视觉特性消费的 SVG group。
 */
const createMount = (name: string, attributes: Record<string, string | number> = {}): SVGGElement =>
    createSVGElement('g', { [`data-text-${name}`]: '', ...attributes });

/**
 * 一次创建 Text Render 输出骨架，并复刻列表与非列表的既有视觉层序。
 *
 * @param renderBox - 已折叠完成的运行时视觉区域。
 * @param filterState - 当前一轮正文 filter 状态。
 * @param snapshot - 当前一轮排版 snapshot，用于确定列表 marker 方向与缩进。
 * @returns SVG 根节点及全部命名挂载点。
 */
export const createSurface = (
    renderBox: BoundingBox,
    filterState: TextFilter,
    snapshot: TextRenderSnapshot,
): TextSurface => {
    const root = createSVGElement('svg', {
        'xmlns:xlink': 'http://www.w3.org/1999/xlink',
        width: renderBox.width,
        height: renderBox.height,
        viewBox: `${renderBox.left} ${renderBox.top} ${renderBox.width} ${renderBox.height}`,
        fill: 'none',
    });

    const hasListMarkerIndent = snapshot.listMarkerIndent > 0;
    const bodyOffset = hasListMarkerIndent ? resolveBodyOffset(snapshot) : null;
    const materialOffset = hasListMarkerIndent ? resolveMaterialOffset(snapshot) : null;
    const background = createMount('background');
    const material = createMount(
        'material',
        materialOffset ? { transform: `translate(${materialOffset.x},${materialOffset.y})` } : {},
    );
    const body = createMount(
        'body',
        bodyOffset
            ? {
                  'data-list-content': '',
                  transform: `translate(${bodyOffset.x},${bodyOffset.y})`,
              }
            : {},
    );
    const characterMarker = createMount('character-marker');
    const graphicMarker = createMount('graphic-marker');

    if (hasListMarkerIndent) {
        root.append(background, material);
    } else {
        root.append(material, background);
    }

    // 正文与字符 marker 共享同一个 filter 祖先；图形型 marker 始终在该祖先之外。
    const effectTarget = createFilterTarget(root, filterState, renderBox);
    effectTarget.append(body, characterMarker);
    root.appendChild(graphicMarker);
    return { root, background, material, body, characterMarker, graphicMarker };
};
